/**
 * Schema migration system untuk SQLite cache.
 *
 * Menggunakan PRAGMA user_version — fitur built-in SQLite untuk melacak
 * versi skema. Setiap migrasi bernomor dan hanya dijalankan jika
 * version > current (docs/04-Database.md §11.1 — backward compatible).
 *
 * Prinsip:
 * - Migrasi hanya menambah (additive), tidak pernah menghapus kolom.
 * - Setiap migrasi bersifat idempoten (CREATE ... IF NOT EXISTS).
 * - Urutan migrasi dijamin oleh array MIGRATIONS (ascending version).
 */

import type { Database as SqlJsDatabase } from 'sql.js';
import { SCHEMA_V1 } from './schema';

// ---------------------------------------------------------------------------
// Migration interface
// ---------------------------------------------------------------------------

export interface Migration {
  /** Nomor versi skema (ascending, unik). */
  version: number;
  /** Deskripsi singkat untuk audit log. */
  description: string;
  /** DDL statements (CREATE TABLE / CREATE INDEX / ALTER TABLE). */
  up: string;
}

// ---------------------------------------------------------------------------
// Daftar migrasi (urutkan ascending by version)
// ---------------------------------------------------------------------------

export const MIGRATIONS: readonly Migration[] = [
  {
    version: 1,
    description: 'Initial cache schema — 11 tables + 4 indexes (S5)',
    up: SCHEMA_V1,
  },
  // Migrasi masa depan ditambah di sini:
  // { version: 2, description: '...', up: '...' },
] as const;

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/** Versi skema terbaru (migrasi terakhir). */
export const LATEST_SCHEMA_VERSION: number = MIGRATIONS[MIGRATIONS.length - 1].version;

/**
 * Baca versi skema saat ini dari database.
 * @returns Nomor versi (0 jika database baru).
 */
export function getCurrentVersion(db: SqlJsDatabase): number {
  const result = db.exec('PRAGMA user_version');
  if (result.length === 0) return 0;
  const value = result[0].values[0]?.[0];
  return typeof value === 'number' ? value : 0;
}

/**
 * Jalankan semua migrasi yang belum diterapkan.
 *
 * Strategi:
 * 1. Baca user_version saat ini.
 * 2. Untuk setiap migrasi dengan version > current:
 *    a. Eksekusi DDL dalam transaksi (BEGIN/COMMIT).
 *    b. Update user_version.
 * 3. Return jumlah migrasi yang dijalankan.
 *
 * @throws Error jika DDL gagal (transaksi di-rollback).
 */
export function runMigrations(db: SqlJsDatabase): {
  applied: number;
  fromVersion: number;
  toVersion: number;
} {
  const fromVersion = getCurrentVersion(db);
  const pending = MIGRATIONS.filter((m) => m.version > fromVersion);

  if (pending.length === 0) {
    return { applied: 0, fromVersion, toVersion: fromVersion };
  }

  for (const migration of pending) {
    try {
      db.run('BEGIN');
      db.run(migration.up);
      db.run(`PRAGMA user_version = ${migration.version}`);
      db.run('COMMIT');
    } catch (error) {
      // Rollback pada kegagalan untuk menjaga konsistensi.
      try {
        db.run('ROLLBACK');
      } catch {
        // Ignore rollback error — transaksi mungkin sudah di-rollback.
      }
      throw new Error(
        `Migration v${migration.version} ("${migration.description}") failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  return { applied: pending.length, fromVersion, toVersion: LATEST_SCHEMA_VERSION };
}
